import React, { useEffect, useState, useMemo } from "react";
import { Table, Typography, Space, Input, message, Button, Modal, Form, Select } from "antd";
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from 'react-redux';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function ImfCountryMappingTab() {
  const [data, setData] = useState([]);
  const [query, setQuery] = useState("");
  const countriesState = useSelector((state) => state.countries);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [editMode, setEditMode] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  useEffect(() => { fetchMappings(); }, []);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const res = await AxiosOICStat.get("/external_api/imf/country-mappings");
      setData(res.data?.mappings || res.data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to load IMF country mappings.";
      message.error(detail);
    } finally { setLoading(false); }
  };

  const handleDelete = (imf_code) => () => {
    Modal.confirm({
      title: 'Delete IMF Country Mapping',
      content: `Are you sure you want to delete the mapping for ${imf_code}?`,
      okText: 'Yes, Delete', okType: 'danger', cancelText: 'Cancel',
      onOk: async () => {
        try {
          await AxiosOICStat.delete(`/external_api/imf/country-mappings`, { data: { geo_area_code: imf_code } });
          setData((prev) => prev.filter((item) => item.imf_code !== imf_code));
          message.success('Deleted mapping');
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || 'Failed to delete mapping.';
          message.error(detail);
        }
      }
    });
  };

  const handleAddSubmit = async () => {
    try {
      const values = await addForm.validateFields();
      setLoading(true);
      if (editMode && editingRecord) {
        await AxiosOICStat.put("/external_api/imf/country-mappings", { geo_area_code: values.imf_code, geo_area_name: values.geo_area_name, c_code: values.c_code });
        message.success("Country mapping updated successfully");
      } else {
        await AxiosOICStat.post("/external_api/imf/country-mappings", { geo_area_code: values.imf_code, geo_area_name: values.geo_area_name, c_code: values.c_code });
        message.success("Country mapping added successfully");
      }

      setAddModalVisible(false);
      addForm.resetFields();
      setEditMode(false);
      setEditingRecord(null);
      fetchMappings();
    } catch (err) {
      if (err.errorFields) return;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to add mapping.';
      message.error(detail);
    } finally { setLoading(false); }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditMode(true);
    setAddModalVisible(true);
    addForm.setFieldsValue({ imf_code: record.imf_code, geo_area_name: record.geo_area, c_code: record.c_code });
  };

  const columns = [
    { title: 'IMF Code', dataIndex: 'imf_code', key: 'imf_code', render: (v) => v ? <Text code>{v}</Text> : <Text type="secondary">—</Text> },
    { title: 'Geo Area Name', dataIndex: 'geo_area', key: 'geo_area', render: (v) => v ? <Text>{v}</Text> : <Text type="secondary">—</Text> },
    { title: 'OIC Country Code', dataIndex: 'c_code', key: 'c_code', render: (v) => <Text strong>{v}</Text> },
    {
      title: 'Actions', key: 'actions', render: (_, record) => (
        <Space>
          <Button type="primary" icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Button type="primary" danger icon={<DeleteOutlined />} size="small" onClick={handleDelete(record.imf_code)} />
        </Space>
      )
    }
  ];

  const availableCountries = countriesState.value
    ? Object.values(countriesState.value)
      .map(c => ({
        ...c,
        displayName: c.c_short_name_eng || c.c_full_name_eng || c.c_name_eng || c.c_name || ''
      }))
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
    : [];

  const filteredData = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return data;
    return data.filter(item => {
      return (item.imf_code || '').toLowerCase().includes(q)
        || (item.geo_area || '').toLowerCase().includes(q)
        || String(item.c_code || '').toLowerCase().includes(q);
    });
  }, [data, query]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space>
        <Search allowClear placeholder="Search by IMF code / geo area / country code…" onSearch={setQuery} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 420 }} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setAddModalVisible(true); setEditMode(false); setEditingRecord(null); }}>Add New Country Mapping</Button>
      </Space>

      <Table rowKey="id" loading={loading} columns={columns} dataSource={filteredData} pagination={{ pageSize: 20, showSizeChanger: true }} bordered />

      <Modal title={editMode ? "Edit IMF Country Mapping" : "Add IMF Country Mapping"} visible={addModalVisible} onOk={handleAddSubmit} onCancel={() => { setAddModalVisible(false); addForm.resetFields(); setEditMode(false); setEditingRecord(null); }} confirmLoading={loading}>
        <Form form={addForm} layout="vertical">
          <Form.Item name="imf_code" label="IMF Code" rules={[{ required: true, message: 'Enter IMF code' }]}>
            <Input disabled={editMode} placeholder="e.g. TUR, SAU, PAK" />
          </Form.Item>
          <Form.Item name="geo_area_name" label="Geo Area Name">
            <Input />
          </Form.Item>
          <Form.Item name="c_code" label="OIC Country Code" rules={[{ required: true, message: 'Select country' }]}>
            <Select showSearch autoClearSearchValue={false} placeholder="Select country" filterOption={(input, option) => [].concat(option?.children ?? '').join('').toLowerCase().includes(input.toLowerCase())}>
              {availableCountries.map(c => <Option key={c.c_code} value={c.c_code}>{c.c_code} - {c.displayName}</Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
